import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Customization } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';

import { useDeleteCustomization } from '../hooks/use-customization-mutations';

export interface DeleteCustomizationDialogProps {
  customization: Customization | null;
  onOpenChange: (open: boolean) => void;
}

/** The backend refuses (409 RESOURCE_IN_USE) while a product or product type still uses it. */
export function DeleteCustomizationDialog({
  customization,
  onOpenChange,
}: DeleteCustomizationDialogProps) {
  const { t } = useTranslation(['dictionary', 'common', 'errors']);
  const remove = useDeleteCustomization();
  const message = useErrorMessage();
  const requestId = isApiError(remove.error) ? remove.error.requestId : undefined;

  return (
    <AlertDialog
      open={customization !== null}
      onOpenChange={(next) => {
        if (!next) remove.reset();
        onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('dictionary:delete.title', { label: customization?.label ?? '' })}
          </AlertDialogTitle>
          <AlertDialogDescription>{t('dictionary:delete.description')}</AlertDialogDescription>
        </AlertDialogHeader>
        {remove.isError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>
              {message(remove.error)}
              {requestId ? (
                <span className="block text-xs">{t('errors:requestIdShort', { requestId })}</span>
              ) : null}
            </AlertDescription>
          </Alert>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={remove.isPending}>
            {t('common:actions.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={remove.isPending}
            onClick={(event) => {
              // Radix closes on click; stay open until the backend has answered.
              event.preventDefault();
              if (customization) {
                remove.mutate(customization.id, { onSuccess: () => onOpenChange(false) });
              }
            }}
          >
            {remove.isPending ? t('dictionary:delete.submitting') : t('dictionary:delete.submit')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
